import { AxiosInstance } from "axios";
import axiosHelper from "../axiosHelper";
import VehicleManufacturer from "@/models/VehicleManufacturer";

export default class VehicleManufacturersApiHelper {

	private _url: string = '/api/vehiclemanufacturers';
	private _httpClient!: AxiosInstance;

	constructor(jwt: string) {
		this._httpClient = axiosHelper.createInstance(jwt);
	}

	async isAlreadyExistAsync(vehicleManufacturerName: string, exceptVehicleManufacturerId?: number) : Promise<boolean | null> {
		try {
			const request = {
				name: vehicleManufacturerName,
				exceptVehicleManufacturerId
			};
			const response = await this._httpClient.post<boolean>(`${this._url}/exist`, request);		
			if (!response.data === null) {
				console.error('Unable to check vehicle manufacturer existence: empty response');
				return null;
			}
			return response.data;
		} catch(e: any) {
			console.error(`Unable to check vehicle manufacturer existence '${vehicleManufacturerName}'`, e);
			return null;
		}
	}

	async isDeletableAsync(vehicleManufacturerId: number) : Promise<boolean | null> {
		try {
			const response = await this._httpClient.get<boolean>(`${this._url}/deletable/${vehicleManufacturerId}`);
			if (!response.data === null) {
				console.error('Unable to check vehicle model: empty response');
				return null;
			}
			return response.data;
		} catch(e: any) {
			console.error(`Unable to check whether vehicle manufaturer is deletable #${vehicleManufacturerId}`, e);
			return null;
		}
	}

	async getManufacturersAsync(
		tableSortColumn: string, 
		tableSortDir: string,
		query: string | null) : Promise<Array<VehicleManufacturer> | null> {
		try {
			const filterObject = {
				orderBy: tableSortColumn,
				orderAsc: tableSortDir == 'ascending',
				query
			};

			const response = await this._httpClient.get(`${this._url}`, { params: filterObject });
			if (!response.data?.length) {
				return [];
			}
			return response.data.map((_: any) => new VehicleManufacturer(_));
		} catch(e) {
			console.error(`Unable to load list of vehicle manufacturers`, e);
			return null;
		}
	}

	async createAsync(request: VehicleManufacturer) : Promise<VehicleManufacturer | null> {
		try {
			const response = await this._httpClient.post(this._url, request);		
			if (!response.data?.id) {
				console.error('Unable to create vehicle manufacturer: empty response');
				return null;
			}
			return new VehicleManufacturer(response.data);			
		} catch(e: any) {
			if (e?.status === 409) {
				console.error(`Unable to create vehicle manufacturer: '${request.name}' already exists`);
				return null;
			}

			console.error('Unable to create vehicle manufacturer', e);
			return null;
		}
	}

	async updateAsync(request: VehicleManufacturer) : Promise<VehicleManufacturer | null> {
		try {			
			const response = await this._httpClient.put(this._url, request);		
			if (!response.data?.id) {
				console.error('Unable to update vehicle manufacturer: empty response');
				return null;
			}
			return new VehicleManufacturer(response.data);
		} catch(e: any) {
			if (e?.status === 409) {
				console.error(`Unable to update vehicle manufacturer #${request.id}: '${request.name}' already exists`);
				return null;
			}

			console.error('Unable to update vehicle model', e);
			return null;
		}
	}

	async getByIdAsync(id: number) : Promise<VehicleManufacturer | null>  {
		try {			
			const respone = await this._httpClient.get(`${this._url}/${id}`);	
			if (!respone.data?.id) {
				return null;
			}
			return new VehicleManufacturer(respone.data);
		} catch(e) {
			console.error(`Unable to get vehicle model by id #${id}`, e);
			return null;
		}
	}

	async deleteAsync(vehicleManufacturerId: number) : Promise<boolean> {
		try {
			await this._httpClient.delete(`${this._url}/${vehicleManufacturerId}`);
			return true;
		} catch(e: any) {
			if (e?.status === 409) {
				console.error(`Unable to delete vehicle manufacturer #${vehicleManufacturerId}: is referenced in orders or has referenced models`);
				return false;
			}
			console.error(`Unable to delete vehicle manufacturer #${vehicleManufacturerId}`, e);
			return false;
		}
	}
}
